'use es6';

import I18n from 'I18n';
import Log from 'forms-utils-lib/logger/Logger';
import { saveFormDraft, publishAndDeleteDraft } from 'FormsUI/api/FormDrafts';
import * as FormsService from 'FormsUI/api/Forms';
import { getDefinition, getPostSubmissionOption, getNotifyRecipients, getUpdatedAt } from 'FormsUI/selectors/FormDefinition';
import { getRedirectUrl, getIsLiveMessagesSettingsRedirectUrl } from 'FormsUI/utils/QueryParams';
import { addValidationErrorIfNeeded, addFieldValidationErrorIfNeeded } from 'FormsUI/actions/FormDefinition';
import { trackUsage } from 'FormsUI/utils/TrackingUtils';
import { navigateTo } from 'FormsUI/utils/UrlUtils';
import PostSubmissionOptions from 'forms-utils-lib/constants/formDefinition/PostSubmissionOptions';
import { ValidationKeys, ValidationType } from 'FormsUI/constants/FormFieldValidations';
import hydrateFormWithDefaults from 'FormsUI/utils/formDefinition/hydrateFormWithDefaults';
import trackFormPublishSuccess from 'FormsUI/actions/TrackUsage/trackFormPublishSuccess';
import { getFormattedDefinition, parseErrorResponse, getDraftErrorOptions, getSaveErrorOptions, validateFormByPostSubmissionOption } from 'FormsUI/utils/FormSavingUtils';
import { closeCurrentDialog } from 'FormsUI/actions/DialogsAndPanels';
import { FormErrors } from 'FormsUI/api/Forms';
import { getFormsCreateUrlForHelpdeskSettings } from 'FormsUI/lib/RouteGenerators';
function createAction(name) {
  return `@@formSaving/${name}`;
}
export const SAVE_FORM = createAction('SAVE_FORM');
export const SAVE_FORM_SUCCESS = createAction('SAVE_FORM_SUCCESS');
export const SAVE_FORM_ERROR = createAction('SAVE_FORM_ERROR');
export const PUBLISH_FORM = createAction('PUBLISH_FORM');
export const PUBLISH_FORM_SUCCESS = createAction('PUBLISH_FORM_SUCCESS');
export const PUBLISH_FORM_ERROR = createAction('PUBLISH_FORM_ERROR');
export const UPDATE_PUBLISH_UPDATE_DATE = createAction('UPDATE_PUBLISH_UPDATE_DATE');
const getPublishSuccessNotification = () => ({
  notification: {
    type: 'success',
    message: I18n.text('editor.form.publishSuccess.formPublishedNotification')
  }
});
const getPublishErrorNotification = (errorType, genericNotification = false) => ({
  notification: {
    type: 'danger',
    title: I18n.text('editor.form.publishError.header'),
    message: !genericNotification && I18n.lookup(`editor.form.savingError.message.${errorType}`) ? I18n.text(`editor.form.savingError.message.${errorType}`) : I18n.text('editor.form.publishError.message.generic')
  }
});
const getSaveFormErrorNotification = (errorType, genericNotification = false) => {
  const getErrorMessage = () => {
    if (!genericNotification && I18n.lookup(`editor.form.savingError.toastErrorMessage.${errorType}`)) {
      return I18n.text(`editor.form.savingError.toastErrorMessage.${errorType}`);
    } else if (!genericNotification && I18n.lookup(`editor.form.savingError.message.${errorType}`)) {
      return I18n.text(`editor.form.savingError.message.${errorType}`);
    }
    return I18n.text('editor.form.savingError.message.generic');
  };
  return {
    notification: {
      type: 'danger',
      title: I18n.text('editor.form.savingError.header'),
      message: getErrorMessage(),
      titleText: !genericNotification && I18n.lookup(`editor.form.savingError.titleText.${errorType}`) ? I18n.lookup(`editor.form.savingError.titleText.${errorType}`) : null
    }
  };
};

// actions

export const saveForm = () => ({
  type: SAVE_FORM
});
export const saveFormSuccess = updatedAt => ({
  type: SAVE_FORM_SUCCESS,
  updatedAt
});
export const updatePublishUpdateDate = (updatedAt, internalUpdatedAt) => ({
  type: UPDATE_PUBLISH_UPDATE_DATE,
  updatedAt,
  internalUpdatedAt
});
export const saveFormError = (errorType, {
  errors = {},
  notification = true,
  genericNotification = false
} = {}) => ({
  type: SAVE_FORM_ERROR,
  errors,
  meta: Object.assign({}, notification ? getSaveFormErrorNotification(errorType, genericNotification) : {})
});
export const publishForm = () => ({
  type: PUBLISH_FORM
});
export const publishFormSuccess = definition => ({
  type: PUBLISH_FORM_SUCCESS,
  definition
});
export const publishFormSuccessWithNotification = definition => ({
  type: PUBLISH_FORM_SUCCESS,
  definition,
  meta: Object.assign({}, getPublishSuccessNotification())
});
export const publishFormError = (errorType, {
  errors = {},
  notification = true,
  genericNotification = false
} = {}) => ({
  type: PUBLISH_FORM_ERROR,
  errors,
  meta: Object.assign({}, notification ? getPublishErrorNotification(errorType, genericNotification) : {})
});
export const validateFormError = errorType => dispatch => {
  switch (errorType) {
    case PostSubmissionOptions.THANK_YOU_MESSAGE:
      return dispatch(addValidationErrorIfNeeded('options', ValidationKeys.THANK_YOU_MESSAGE, ValidationType.REQUIRED));
    case PostSubmissionOptions.REDIRECT_PAGE:
      return dispatch(addValidationErrorIfNeeded('options', ValidationKeys.REDIRECT_PAGE, ValidationType.REQUIRED));
    default:
      return null;
  }
};
export const saveFormDraftError = xhr => dispatch => {
  const {
    errorType,
    responseText
  } = parseErrorResponse(xhr, 'SAVE_DRAFT');
  const {
    options,
    fieldErrorType
  } = getDraftErrorOptions({
    errorType,
    responseText
  });
  dispatch(saveFormError(fieldErrorType, options));
};
export const saveFormDefinition = formGuid => (dispatch, getState) => {
  const formDefinition = getDefinition(getState());
  const postSubmissionOption = getPostSubmissionOption(getState());
  const recipients = getNotifyRecipients(getState());
  const {
    valid,
    errorType
  } = validateFormByPostSubmissionOption(formDefinition, postSubmissionOption);
  if (!valid) {
    dispatch(validateFormError(errorType));
    return Promise.resolve();
  }
  const formattedFormDefinition = getFormattedDefinition(postSubmissionOption, formDefinition, recipients);
  const knownUpdatedAt = getUpdatedAt(getState());
  dispatch(saveForm());
  return saveFormDraft(formGuid, formattedFormDefinition, knownUpdatedAt).then(({
    updatedAt
  }) => {
    dispatch(saveFormSuccess(updatedAt));
  }).catch(({
    xhr
  }) => {
    dispatch(saveFormDraftError(xhr));
  });
};
export const updatePublishFormErrorValidation = formFieldValidationErrors => dispatch => {
  const errorsArr = Object.keys(formFieldValidationErrors);
  errorsArr.forEach(key => formFieldValidationErrors[key].forEach(field => dispatch(addFieldValidationErrorIfNeeded(field.fieldName, field.objectType, ValidationKeys[field.formFieldValidationErrorOption], ValidationType.INVALID, {}))));
};
export const publishFormDefinitionSuccess = ({
  formDefinition,
  withNotification,
  formRouting
}) => dispatch => {
  const {
    updatedAt,
    internalUpdatedAt
  } = formDefinition;
  const formDefinitionHydrated = hydrateFormWithDefaults(formDefinition);
  dispatch(trackFormPublishSuccess(formRouting));
  if (withNotification) {
    dispatch(publishFormSuccessWithNotification(formDefinitionHydrated));
  } else {
    dispatch(publishFormSuccess(formDefinitionHydrated));
  }
  dispatch(updatePublishUpdateDate(updatedAt, internalUpdatedAt));
};
export const publishFormDefinitionError = xhr => dispatch => {
  const {
    errorType,
    responseText
  } = parseErrorResponse(xhr, 'PUBLISH');
  const options = getSaveErrorOptions({
    errorType,
    responseText
  });
  switch (errorType) {
    case FormErrors.FORM_FIELDS_NOT_VALID:
      dispatch(closeCurrentDialog());
      break;
    default:
      break;
  }
  dispatch(publishFormError(errorType, options));
};
export const publishFormDefinition = (formGuid, {
  withNotification = true,
  formRouting = undefined
} = {}) => (dispatch, getState) => {
  const formDefinition = getDefinition(getState());
  const postSubmissionOption = getPostSubmissionOption(getState());
  const recipients = getNotifyRecipients(getState());
  const {
    valid,
    errorType
  } = validateFormByPostSubmissionOption(formDefinition, postSubmissionOption);
  if (!valid) {
    return dispatch(validateFormError(errorType));
  }
  trackUsage('update-existing-form');
  const formattedFormDefinition = getFormattedDefinition(postSubmissionOption, formDefinition, recipients);
  dispatch(publishForm());
  return publishAndDeleteDraft(formGuid, formattedFormDefinition).then(formDefinitionReturn => {
    dispatch(publishFormDefinitionSuccess({
      formDefinition: formDefinitionReturn,
      withNotification,
      formRouting
    }));
  }).catch(error => {
    const {
      xhr
    } = error;
    if (xhr.status_code !== 412) {
      Log.error('Could not publish and delete form', xhr);
    }
    dispatch(publishFormDefinitionError(xhr));
    throw error;
  });
};
export const publishFormDefinitionAndCosRedirect = ({
  formGuid,
  formRouting,
  hasRequiredTicketFields,
  ungatedForLimitedTicketFunctionalityInInbox,
  hasHelpDeskAccess
}) => (dispatch, getState) => {
  dispatch(publishFormDefinition(formGuid, {
    formRouting
  })).then(() => {
    if (hasRequiredTicketFields && ungatedForLimitedTicketFunctionalityInInbox && getIsLiveMessagesSettingsRedirectUrl() && hasHelpDeskAccess) {
      const helpdeskFormsCreateUrl = getFormsCreateUrlForHelpdeskSettings();
      const fullUrl = `${helpdeskFormsCreateUrl}?supportFormId=${formGuid}&newForm=true`;
      navigateTo(fullUrl);
    } else {
      navigateTo(getRedirectUrl(getState()));
    }
  }).catch(err => {
    setTimeout(() => {
      throw err;
    });
  });
};
export const saveFormDefinitionAndRedirect = formGuid => {
  return (dispatch, getState) => {
    dispatch(publishFormDefinition(formGuid)).then(() => {
      navigateTo(getRedirectUrl(getState()));
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  };
};
export const saveFormNameError = xhr => dispatch => {
  const {
    errorType,
    responseText
  } = parseErrorResponse(xhr, 'SAVE_DRAFT');
  const options = getSaveErrorOptions({
    errorType,
    responseText
  });
  dispatch(saveFormError(errorType, options));
};
export const saveFormName = (formGuid, name) => dispatch => {
  FormsService.renameForm(formGuid, {
    name
  }).then(() => {
    dispatch(saveFormSuccess());
  }).catch(err => {
    dispatch(saveFormNameError(err.xhr));
    setTimeout(() => {
      throw err;
    });
  });
};